import Login from "./Login";
import React, { useState, useEffect, useRef } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import GanttChart from "./GanttChart";
import AIGanttBuilder from "./AIGanttBuilder";
import MisDocumentos from './MisDocumentos';
import Dashboard from './Dashboard';
import KnowledgeBase from "./KnowledgeBase";
import DashboardCamaron from "./DashboardCamaron";
import DashboardOperativo from "./DashboardOperativo";
import ParticipantRoles from "./ParticipantRoles";
import DiplomadoChat from './DiplomadoChat';
import AlimareNY from './AlimareNY';

const COLORS = ["#3B82F6", "#10B981", "#F59E0B", "#EF4444", "#8B5CF6"];
const API_URL = "https://danielcantos.net/api";
const get = (url) => fetch(API_URL + url).then(r => r.json());
const post = (url, data) => fetch(API_URL + url, {method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify(data)}).then(r => r.json());
const del = (url) => fetch(API_URL + url, {method:"DELETE"});

// ── Logo SVG nodo de red ──
const LogoIcon = () => (
  <svg width="34" height="34" viewBox="0 0 36 36" fill="none">
    <circle cx="18" cy="18" r="4" fill="#D4AF37" opacity="0.95"/>
    <circle cx="6"  cy="10" r="2.5" fill="#D4AF37" opacity="0.55"/>
    <circle cx="30" cy="10" r="2.5" fill="#D4AF37" opacity="0.55"/>
    <circle cx="6"  cy="26" r="2.5" fill="#D4AF37" opacity="0.55"/>
    <circle cx="30" cy="26" r="2.5" fill="#D4AF37" opacity="0.55"/>
    <circle cx="18" cy="4"  r="2"   fill="#D4AF37" opacity="0.3"/>
    <circle cx="18" cy="32" r="2"   fill="#D4AF37" opacity="0.3"/>
    <line x1="18" y1="18" x2="6"  y2="10" stroke="#D4AF37" strokeWidth="0.9" opacity="0.45"/>
    <line x1="18" y1="18" x2="30" y2="10" stroke="#D4AF37" strokeWidth="0.9" opacity="0.45"/>
    <line x1="18" y1="18" x2="6"  y2="26" stroke="#D4AF37" strokeWidth="0.9" opacity="0.45"/>
    <line x1="18" y1="18" x2="30" y2="26" stroke="#D4AF37" strokeWidth="0.9" opacity="0.45"/>
    <line x1="18" y1="18" x2="18" y2="4"  stroke="#D4AF37" strokeWidth="0.9" opacity="0.25"/>
    <line x1="18" y1="18" x2="18" y2="32" stroke="#D4AF37" strokeWidth="0.9" opacity="0.25"/>
    <line x1="6"  y1="10" x2="30" y2="10" stroke="#D4AF37" strokeWidth="0.5" opacity="0.12"/>
    <line x1="6"  y1="26" x2="30" y2="26" stroke="#D4AF37" strokeWidth="0.5" opacity="0.12"/>
  </svg>
);

// ── Estilos globales ──
const NAV_BG    = "linear-gradient(135deg, #0d1b3e 0%, #0a1628 100%)";
const GOLD      = "#D4AF37";
const GOLD_DIM  = "rgba(212,175,55,0.15)";
const GOLD_BOR  = "rgba(212,175,55,0.25)";
const WHITE_DIM = "rgba(255,255,255,0.55)";
const WHITE_MED = "rgba(255,255,255,0.80)";
const PAGE_BG   = "#0a1220";

const navTab = (active) => ({
  display:"flex", alignItems:"center", gap:6,
  padding:"6px 13px", borderRadius:6,
  fontSize:12.5, fontWeight:500, cursor:"pointer",
  border: active ? `1px solid ${GOLD_BOR}` : "1px solid transparent",
  background: active ? GOLD_DIM : "transparent",
  color: active ? GOLD : WHITE_DIM,
  whiteSpace:"nowrap",
});

const NAV_LABELS = {
  gantt:"📊 Gantt", financials:"💰 Finanzas", kpis:"🎯 KPIs",
  ai:"🤖 Análisis", chat:"💬 Chat IA", knowledge:"🧠 Base de Conocimiento",
  dashboard:"📈 Dashboard", operativo:"🏭 Operativo",
};

const inp = {border:"1px solid #CBD5E1",borderRadius:8,padding:"8px 12px",fontSize:14,width:"100%"};
const btn = {background:"#2563EB",color:"white",border:"none",borderRadius:8,padding:"10px 20px",cursor:"pointer",fontWeight:600};
const btnG = {background:"#F1F5F9",color:"#475569",border:"none",borderRadius:8,padding:"10px 20px",cursor:"pointer"};
const card = {background:"white",borderRadius:12,padding:20,boxShadow:"0 2px 8px rgba(0,0,0,0.06)",border:"1px solid #E2E8F0"};
const darkCard = {
  background:"rgba(255,255,255,0.03)",
  border:"1px solid rgba(255,255,255,0.07)",
  borderRadius:12, padding:20,
};

// ── Dominios a filtrar (sistema/notificaciones) ──
const SYSTEM_SENDERS = [
  "brevo", "sendinblue", "no-reply", "noreply", "donotreply",
  "notifications", "notificaciones", "mailer-daemon", "postmaster",
  "bounce", "support@", "info@anthropic", "projectdcantosia"
];
const isSystemEmail = (email) => {
  if (!email) return false;
  const lower = email.toLowerCase();
  return SYSTEM_SENDERS.some(s => lower.includes(s));
};

// ── Componente Panel Inbox ──
function InboxPanel({ onClose }) {
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState("");
  const [draftLoading, setDraftLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendSuccess, setSendSuccess] = useState("");
  const [editMode, setEditMode] = useState(false);

  // ── Nuevo correo ──
  const [showNewEmail, setShowNewEmail] = useState(false);
  const [newTo, setNewTo] = useState("");
  const [newSubject, setNewSubject] = useState("");
  const [newBody, setNewBody] = useState("");
  const [newDraftLoading, setNewDraftLoading] = useState(false);
  const [newSendLoading, setNewSendLoading] = useState(false);
  const [newSendSuccess, setNewSendSuccess] = useState("");
  const [newEditMode, setNewEditMode] = useState(false);
  const [activeTab, setActiveTab] = useState("recibidos");
  const [sentEmails, setSentEmails] = useState([]);
  const [sentLoading, setSentLoading] = useState(false);
  const [selectedSent, setSelectedSent] = useState(null);
  const [sentBody, setSentBody] = useState("");
  const [sentBodyLoading, setSentBodyLoading] = useState(false);

  useEffect(() => { loadInbox(); }, []);

  const loadInbox = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await get("/email/inbox");
      // Filtrar correos de sistema
      const real = (data.messages || data.emails || []).filter(e => !isSystemEmail(e.from || e.sender || e.from_email || ""));
      setEmails(real);
    } catch(e) {
      setError("No se pudo conectar con Gmail");
    }
    setLoading(false);
  };

  const loadSent = async () => {
    setSentLoading(true);
    try {
      const data = await get("/email/sent");
      setSentEmails(data.messages || []);
    } catch(e) {}
    setSentLoading(false);
  };

  const loadDraft = async (email) => {
    setSelected(email);
    setDraft("");
    setEditMode(false);
    setSendSuccess("");
    setDraftLoading(true);
    try {
      const data = await get(`/email/message/${email.id}/draft-reply`);
      setDraft(data.draft || data.response || data.reply || "");
    } catch(e) {
      setDraft("Error al generar borrador.");
    }
    setDraftLoading(false);
  };

  const sendReply = async () => {
    if (!selected || !draft.trim()) return;
    setSendLoading(true);
    setSendSuccess("");
    try {
      const result = await post("/email/reply/send", {
        message_id: selected.id,
        thread_id: selected.thread_id,
        subject: selected.subject || "(Sin asunto)",
        body: draft,
        reply: draft,
        to: selected.from || selected.sender || selected.from_email,
      });
      if (result && (result.status || result.success || result.message_id)) {
        setSendSuccess("✅ Respuesta enviada correctamente");
        setTimeout(() => { setSendSuccess(""); setSelected(null); loadInbox(); }, 2500);
      } else {
        setSendSuccess("❌ Error al enviar");
      }
    } catch(e) {
      setSendSuccess("❌ Error al enviar");
    }
    setSendLoading(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    try {
      return new Date(dateStr).toLocaleString("es-EC", {month:"short", day:"numeric", hour:"2-digit", minute:"2-digit"});
    } catch { return dateStr; }
  };

  const draftNewEmail = async () => {
    if (!newTo.trim() || !newSubject.trim()) return alert("Ingresa destinatario y asunto");
    setNewDraftLoading(true);
    setNewBody("");
    setNewEditMode(false);
    try {
      const data = await post("/email/draft-new", {
        to: newTo,
        subject: newSubject,
        context: newBody,
      });
      setNewBody(data.draft || data.body || "");
    } catch(e) {
      setNewBody("Error al generar borrador.");
    }
    setNewDraftLoading(false);
  };

  const sendNewEmail = async () => {
    if (!newTo.trim() || !newSubject.trim() || !newBody.trim()) return;
    setNewSendLoading(true);
    setNewSendSuccess("");
    try {
      const result = await post("/email/send", {
        to: newTo.split(",").map(e => e.trim()).filter(Boolean),
        subject: newSubject,
        body: newBody,
      });
      if (result) {
        setNewSendSuccess("✅ Correo enviado correctamente");
        setTimeout(() => {
          setNewSendSuccess("");
          setShowNewEmail(false);
          setNewTo(""); setNewSubject(""); setNewBody("");
        }, 2500);
      }
    } catch(e) {
      setNewSendSuccess("❌ Error al enviar");
    }
    setNewSendLoading(false);
  };

  return (
    <div style={{
      position:"fixed", top:0, left:0, width:"100%", height:"100%",
      background:"rgba(0,0,0,0.75)", zIndex:9998,
      display:"flex", alignItems:"center", justifyContent:"center",
    }}>
      <div style={{
        background:"#0d1b3e",
        border:`1px solid ${GOLD_BOR}`,
        borderRadius:16,
        width:"min(900px, 96vw)",
        height:"80vh",
        display:"flex",
        flexDirection:"column",
        boxShadow:"0 16px 64px rgba(0,0,0,0.7)",
        overflow:"hidden",
      }}>
        {/* Header */}
        <div style={{
          display:"flex", alignItems:"center", justifyContent:"space-between",
          padding:"16px 24px",
          borderBottom:`1px solid ${GOLD_BOR}`,
          background:"rgba(212,175,55,0.05)",
        }}>
          <div style={{display:"flex", alignItems:"center", gap:10}}>
            <span style={{fontSize:20}}>📬</span>
            <div>
              <div style={{fontWeight:700, color: WHITE_MED, fontSize:15}}>Correos</div>
              <div style={{fontSize:11, color:"rgba(255,255,255,0.3)"}}>projectdcantosia@gmail.com</div>
            </div>
            <div style={{display:"flex", gap:4, marginLeft:16}}>
              <button onClick={() => { setActiveTab("recibidos"); setSelected(null); }} style={{
                padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600, cursor:"pointer",
                background: activeTab==="recibidos" ? GOLD : "rgba(255,255,255,0.06)",
                color: activeTab==="recibidos" ? "#0d1b3e" : "rgba(255,255,255,0.5)",
                border:"none",
              }}>📥 Recibidos</button>
              <button onClick={() => { setActiveTab("enviados"); setSelected(null); loadSent(); }} style={{
                padding:"4px 12px", borderRadius:20, fontSize:11, fontWeight:600, cursor:"pointer",
                background: activeTab==="enviados" ? GOLD : "rgba(255,255,255,0.06)",
                color: activeTab==="enviados" ? "#0d1b3e" : "rgba(255,255,255,0.5)",
                border:"none",
              }}>📤 Enviados</button>
            </div>
          </div>
          <div style={{display:"flex", gap:8, alignItems:"center"}}>
            <button onClick={() => setShowNewEmail(true)} style={{
              padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:600,
              background: GOLD_DIM, color: GOLD, border:`1px solid ${GOLD_BOR}`, cursor:"pointer",
            }}>✉️ Nuevo Correo</button>
            <button onClick={loadInbox} style={{
              padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:600,
              background:"rgba(255,255,255,0.06)", color: WHITE_DIM, border:"none", cursor:"pointer",
            }}>🔄 Actualizar</button>
            <button onClick={onClose} style={{
              padding:"6px 14px", borderRadius:6, fontSize:12, fontWeight:600,
              background:"rgba(231,76,60,0.15)", color:"#e74c3c", border:"1px solid rgba(231,76,60,0.25)", cursor:"pointer",
            }}>✕ Cerrar</button>
          </div>
        </div>

        {/* Body — dos columnas */}
        <div style={{display:"flex", flex:1, overflow:"hidden"}}>

          {/* Lista de correos */}
          <div style={{
            width: (selected || selectedSent) ? "42%" : "100%",
            borderRight: (selected || selectedSent) ? `1px solid rgba(255,255,255,0.07)` : "none",
            overflowY:"auto",
            transition:"width 0.2s",
          }}>
            {loading && (
              <div style={{textAlign:"center", padding:40}}>
                <div style={{fontSize:28, marginBottom:12}}>⏳</div>
                <div style={{color:"rgba(255,255,255,0.4)", fontSize:13}}>Cargando correos...</div>
              </div>
            )}
            {error && (
              <div style={{textAlign:"center", padding:40}}>
                <div style={{fontSize:28, marginBottom:12}}>⚠️</div>
                <div style={{color:"#e74c3c", fontSize:13}}>{error}</div>
                <button onClick={loadInbox} style={{marginTop:12, ...btn, background: GOLD, color:"#0d1b3e", padding:"8px 16px", fontSize:12}}>Reintentar</button>
              </div>
            )}
            {!loading && !error && emails.length === 0 && (
              <div style={{textAlign:"center", padding:60}}>
                <div style={{fontSize:36, marginBottom:12}}>📭</div>
                <div style={{color: WHITE_MED, fontWeight:600, marginBottom:8}}>Sin correos nuevos</div>
                <div style={{color:"rgba(255,255,255,0.3)", fontSize:13}}>No hay mensajes sin leer de contactos externos</div>
              </div>
            )}
            {activeTab === "enviados" && sentLoading && (
              <div style={{textAlign:"center", padding:40}}>
                <div style={{fontSize:28, marginBottom:12}}>⏳</div>
                <div style={{color:"rgba(255,255,255,0.4)", fontSize:13}}>Cargando enviados...</div>
              </div>
            )}
            {activeTab === "enviados" && !sentLoading && sentEmails.length === 0 && (
              <div style={{textAlign:"center", padding:60}}>
                <div style={{fontSize:36, marginBottom:12}}>📤</div>
                <div style={{color: WHITE_MED, fontWeight:600, marginBottom:8}}>Sin enviados</div>
              </div>
            )}
            {activeTab === "enviados" && !sentLoading && sentEmails.map((email, i) => (
              <div
                key={email.id || i}
                onClick={() => {
                  setSelectedSent(email);
                  setSelected(null);
                  setSentBody("");
                  setSentBodyLoading(true);
                  if (email.source === "brevo") {
                    get(`/email/sent-body?to=${encodeURIComponent(email.to)}&subject=${encodeURIComponent(email.subject || "")}`).then(r => {
                      setSentBody(r.body || "");
                      setSentBodyLoading(false);
                    }).catch(() => setSentBodyLoading(false));
                  } else {
                    get(`/email/message/${email.id}/full`).then(r => {
                      setSentBody(r.body || "");
                      setSentBodyLoading(false);
                    }).catch(() => setSentBodyLoading(false));
                  }
                }}
                style={{
                padding:"14px 20px",
                borderBottom:"1px solid rgba(255,255,255,0.04)",
                cursor:"pointer",
                background: selectedSent?.id === email.id ? "rgba(212,175,55,0.08)" : "transparent",
                borderLeft: selectedSent?.id === email.id ? `3px solid ${GOLD}` : "3px solid transparent",
              }}>
                <div style={{display:"flex", justifyContent:"space-between", marginBottom:4}}>
                  <div style={{fontSize:12, color: GOLD, fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"60%"}}>
                    Para: {email.to || ""}
                  </div>
                  <div style={{fontSize:11, color:"rgba(255,255,255,0.3)", whiteSpace:"nowrap"}}>
                    {email.date ? new Date(email.date).toLocaleString("es-EC",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : ""}
                  </div>
                </div>
                <div style={{fontSize:13, color: WHITE_MED, fontWeight:600, marginBottom:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {email.subject || "(Sin asunto)"}
                </div>
                <div style={{fontSize:12, color:"rgba(255,255,255,0.4)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap"}}>
                  {email.snippet || ""}
                </div>
              </div>
            ))}
            {activeTab === "recibidos" && !loading && !error && emails.map((email, i) => (
              <div
                key={email.id || i}
                onClick={() => { setSelectedSent(null); loadDraft(email); }}
                style={{
                  padding:"14px 20px",
                  borderBottom:`1px solid rgba(255,255,255,0.04)`,
                  cursor:"pointer",
                  background: selected?.id === email.id ? "rgba(212,175,55,0.08)" : "transparent",
                  borderLeft: selected?.id === email.id ? `3px solid ${GOLD}` : "3px solid transparent",
                  transition:"all 0.15s",
                }}
                onMouseEnter={e => { if (selected?.id !== email.id) e.currentTarget.style.background = "rgba(255,255,255,0.03)"; }}
                onMouseLeave={e => { if (selected?.id !== email.id) e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{display:"flex", justifyContent:"space-between", marginBottom:4}}>
                  <span style={{
                    fontWeight:700, fontSize:13, color: WHITE_MED,
                    overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"70%",
                  }}>
                    {email.from_name || email.sender_name || (email.from || email.sender || "").split("<")[0].trim() || "Desconocido"}
                  </span>
                  <span style={{fontSize:11, color:"rgba(255,255,255,0.3)", whiteSpace:"nowrap"}}>
                    {formatDate(email.date || email.received_at)}
                  </span>
                </div>
                <div style={{
                  fontSize:12, fontWeight:600, color:"rgba(255,255,255,0.6)",
                  marginBottom:3, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                }}>
                  {email.subject || "(Sin asunto)"}
                </div>
                <div style={{
                  fontSize:11, color:"rgba(255,255,255,0.3)",
                  overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap",
                }}>
                  {email.snippet || email.preview || email.body?.slice(0, 100) || ""}
                </div>
              </div>
            ))}
          </div>

          {/* Panel de respuesta */}
          {selected && (
            <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>

              {/* Info del correo seleccionado */}
              <div style={{
                padding:"14px 20px",
                borderBottom:`1px solid rgba(255,255,255,0.07)`,
                background:"rgba(255,255,255,0.02)",
              }}>
                <div style={{fontWeight:700, color: WHITE_MED, fontSize:14, marginBottom:4}}>
                  {selected.subject || "(Sin asunto)"}
                </div>
                <div style={{fontSize:12, color:"rgba(255,255,255,0.35)"}}>
                  De: {selected.from || selected.sender || selected.from_email || "Desconocido"} · {formatDate(selected.date || selected.received_at)}
                </div>
                {selected.snippet && (
                  <div style={{
                    marginTop:8, fontSize:12, color:"rgba(255,255,255,0.45)",
                    lineHeight:1.5, padding:"8px 12px",
                    background:"rgba(255,255,255,0.03)", borderRadius:6,
                    borderLeft:`2px solid rgba(255,255,255,0.1)`,
                  }}>
                    {selected.snippet}
                  </div>
                )}
              </div>

              {/* Borrador del agente */}
              <div style={{flex:1, padding:"16px 20px", overflowY:"auto", display:"flex", flexDirection:"column", gap:12}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center"}}>
                  <div style={{display:"flex", alignItems:"center", gap:6}}>
                    <span style={{fontSize:13, fontWeight:600, color: GOLD}}>🤖 Borrador del Agente</span>
                    {draftLoading && <span style={{fontSize:11, color:"rgba(255,255,255,0.3)"}}>Redactando...</span>}
                  </div>
                  {!draftLoading && draft && (
                    <button
                      onClick={() => setEditMode(!editMode)}
                      style={{
                        padding:"4px 10px", fontSize:11, fontWeight:600, borderRadius:5,
                        background: editMode ? GOLD_DIM : "rgba(255,255,255,0.06)",
                        color: editMode ? GOLD : WHITE_DIM,
                        border: editMode ? `1px solid ${GOLD_BOR}` : "1px solid rgba(255,255,255,0.1)",
                        cursor:"pointer",
                      }}>
                      {editMode ? "👁 Ver preview" : "✏️ Editar"}
                    </button>
                  )}
                </div>

                {draftLoading && (
                  <div style={{textAlign:"center", padding:30}}>
                    <div style={{fontSize:24, marginBottom:8}}>🤖</div>
                    <div style={{color:GOLD, fontSize:13}}>El agente está redactando la respuesta...</div>
                  </div>
                )}

                {!draftLoading && draft && (
                  editMode ? (
                    <textarea
                      value={draft}
                      onChange={e => setDraft(e.target.value)}
                      style={{
                        flex:1, width:"100%", minHeight:220,
                        background:"rgba(255,255,255,0.04)",
                        border:`1px solid ${GOLD_BOR}`,
                        borderRadius:8, padding:"12px 14px",
                        fontSize:13, color: WHITE_MED,
                        lineHeight:1.6, resize:"vertical",
                        outline:"none", boxSizing:"border-box",
                        fontFamily:"Inter, sans-serif",
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        background:"rgba(255,255,255,0.03)",
                        border:`1px solid rgba(255,255,255,0.07)`,
                        borderRadius:8, padding:"14px 16px",
                        fontSize:13, color: WHITE_MED,
                        lineHeight:1.7,
                        flex:1, overflowY:"auto",
                      }}
                      dangerouslySetInnerHTML={{
                        __html: draft.includes("<") ? draft : draft.replace(/\n/g, "<br/>")
                      }}
                    />
                  )
                )}

                {sendSuccess && (
                  <div style={{
                    padding:"10px 14px", borderRadius:8, fontSize:13, fontWeight:600,
                    background: sendSuccess.startsWith("✅") ? "rgba(46,204,113,0.1)" : "rgba(231,76,60,0.1)",
                    color: sendSuccess.startsWith("✅") ? "#2ecc71" : "#e74c3c",
                    border: `1px solid ${sendSuccess.startsWith("✅") ? "rgba(46,204,113,0.25)" : "rgba(231,76,60,0.25)"}`,
                    textAlign:"center",
                  }}>
                    {sendSuccess}
                  </div>
                )}
              </div>

              {/* Botones de acción */}
              {!draftLoading && draft && (
                <div style={{
                  padding:"14px 20px",
                  borderTop:`1px solid rgba(255,255,255,0.07)`,
                  display:"flex", gap:8, justifyContent:"flex-end",
                  background:"rgba(255,255,255,0.02)",
                }}>
                  <button
                    onClick={() => { setSelected(null); setDraft(""); setEditMode(false); }}
                    style={{
                      padding:"8px 18px", borderRadius:7, fontSize:13, fontWeight:600,
                      background:"rgba(255,255,255,0.06)", color: WHITE_DIM, border:"none", cursor:"pointer",
                    }}>
                    Cancelar
                  </button>
                  <button
                    onClick={sendReply}
                    disabled={sendLoading}
                    style={{
                      padding:"8px 22px", borderRadius:7, fontSize:13, fontWeight:700,
                      background: GOLD, color:"#0d1b3e", border:"none",
                      cursor: sendLoading ? "not-allowed" : "pointer",
                      opacity: sendLoading ? 0.7 : 1,
                    }}>
                    {sendLoading ? "Enviando..." : "✉️ Enviar Respuesta"}
                  </button>
                </div>
              )}
            </div>
          )}

          {selectedSent && (
            <div style={{flex:1, display:"flex", flexDirection:"column", overflow:"hidden"}}>
              <div style={{
                padding:"14px 20px",
                borderBottom:`1px solid rgba(255,255,255,0.07)`,
                background:"rgba(255,255,255,0.02)",
                display:"flex", justifyContent:"space-between", alignItems:"flex-start",
              }}>
                <div>
                  <div style={{fontWeight:700, color: WHITE_MED, fontSize:14, marginBottom:4}}>
                    {selectedSent.subject || "(Sin asunto)"}
                  </div>
                  <div style={{fontSize:12, color:"rgba(255,255,255,0.35)"}}>
                    Para: {selectedSent.to || "Desconocido"} · {formatDate(selectedSent.date)}
                  </div>
                </div>
                <button onClick={() => { setSelectedSent(null); setSentBody(""); }}
                  style={{padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, background:"rgba(255,255,255,0.06)", color: WHITE_DIM, border:"none", cursor:"pointer"}}>
                  ✕ Cerrar
                </button>
              </div>
              <div style={{flex:1, padding:"16px 20px", overflowY:"auto"}}>
                {sentBodyLoading && <div style={{color:"rgba(255,255,255,0.4)", fontSize:13}}>Cargando contenido...</div>}
                {!sentBodyLoading && (
                  <div style={{
                    background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.07)",
                    borderRadius:8, padding:"14px 16px", fontSize:13, color: WHITE_MED,
                    lineHeight:1.7, whiteSpace:"pre-wrap",
                  }}>
                    {sentBody || selectedSent.snippet || "(Sin contenido disponible para este correo)"}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── MODAL NUEVO CORREO ── */}
      {showNewEmail && (
        <div style={{
          position:"fixed", top:0, left:0, width:"100%", height:"100%",
          background:"rgba(0,0,0,0.6)", zIndex:10000,
          display:"flex", alignItems:"center", justifyContent:"center",
        }}>
          <div style={{
            background:"#0d1b3e",
            border:`1px solid ${GOLD_BOR}`,
            borderRadius:16, padding:28,
            width:"min(620px, 94vw)",
            maxHeight:"88vh", overflowY:"auto",
            boxShadow:"0 16px 64px rgba(0,0,0,0.8)",
          }}>
            {/* Header */}
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20}}>
              <div style={{display:"flex", alignItems:"center", gap:8}}>
                <span style={{fontSize:18}}>✉️</span>
                <div>
                  <div style={{fontWeight:700, color: WHITE_MED, fontSize:15}}>Nuevo Correo</div>
                  <div style={{fontSize:11, color:"rgba(255,255,255,0.3)"}}>DC Project Intelligence</div>
                </div>
              </div>
              <button onClick={() => { setShowNewEmail(false); setNewTo(""); setNewSubject(""); setNewBody(""); setNewSendSuccess(""); }}
                style={{padding:"5px 12px", borderRadius:6, fontSize:12, fontWeight:600, background:"rgba(231,76,60,0.15)", color:"#e74c3c", border:"1px solid rgba(231,76,60,0.25)", cursor:"pointer"}}>
                ✕ Cerrar
              </button>
            </div>

            {/* Para */}
            <div style={{marginBottom:12}}>
              <label style={{fontSize:11, color: GOLD, letterSpacing:"1px", textTransform:"uppercase", display:"block", marginBottom:5}}>Para</label>
              <input
                placeholder="correo@ejemplo.com, correo2@ejemplo.com"
                value={newTo}
                onChange={e => setNewTo(e.target.value)}
                style={{width:"100%", padding:"9px 13px", background:"rgba(255,255,255,0.05)", border:`1px solid ${GOLD_BOR}`, borderRadius:8, fontSize:13, color: WHITE_MED, outline:"none", boxSizing:"border-box"}}
              />
            </div>

            {/* Asunto */}
            <div style={{marginBottom:16}}>
              <label style={{fontSize:11, color: GOLD, letterSpacing:"1px", textTransform:"uppercase", display:"block", marginBottom:5}}>Asunto</label>
              <input
                placeholder="Asunto del correo"
                value={newSubject}
                onChange={e => setNewSubject(e.target.value)}
                style={{width:"100%", padding:"9px 13px", background:"rgba(255,255,255,0.05)", border:`1px solid ${GOLD_BOR}`, borderRadius:8, fontSize:13, color: WHITE_MED, outline:"none", boxSizing:"border-box"}}
              />
            </div>

            {/* Botón IA */}
            {!newBody && (
              <button onClick={draftNewEmail} disabled={newDraftLoading}
                style={{width:"100%", padding:"11px", background: GOLD, color:"#0d1b3e", border:"none", borderRadius:8, cursor:"pointer", fontWeight:700, fontSize:14, opacity:newDraftLoading?0.7:1, marginBottom:14}}>
                {newDraftLoading ? "🤖 Redactando con IA..." : "🤖 Redactar con IA"}
              </button>
            )}

            {/* Cuerpo */}
            {(newBody || newDraftLoading) && (
              <div style={{marginBottom:14}}>
                <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:8}}>
                  <label style={{fontSize:11, color: GOLD, letterSpacing:"1px", textTransform:"uppercase"}}>
                    {newEditMode ? "✏️ Editando" : "👁 Preview"}
                  </label>
                  <div style={{display:"flex", gap:6}}>
                    {newBody && (
                      <button onClick={() => setNewEditMode(!newEditMode)}
                        style={{fontSize:11, padding:"3px 10px", borderRadius:5, fontWeight:600, cursor:"pointer",
                          background: newEditMode ? GOLD_DIM : "rgba(255,255,255,0.06)",
                          color: newEditMode ? GOLD : WHITE_DIM,
                          border: newEditMode ? `1px solid ${GOLD_BOR}` : "1px solid rgba(255,255,255,0.1)"}}>
                        {newEditMode ? "👁 Preview" : "✏️ Editar"}
                      </button>
                    )}
                    {newBody && (
                      <button onClick={() => { setNewBody(""); setNewEditMode(false); }}
                        style={{fontSize:11, padding:"3px 10px", borderRadius:5, fontWeight:600, cursor:"pointer", background:"rgba(255,255,255,0.06)", color: WHITE_DIM, border:"1px solid rgba(255,255,255,0.1)"}}>
                        Regenerar
                      </button>
                    )}
                  </div>
                </div>
                {newDraftLoading && (
                  <div style={{textAlign:"center", padding:24}}>
                    <div style={{fontSize:22, marginBottom:8}}>🤖</div>
                    <div style={{color: GOLD, fontSize:13}}>El agente está redactando...</div>
                  </div>
                )}
                {!newDraftLoading && newBody && (
                  newEditMode ? (
                    <textarea value={newBody} onChange={e => setNewBody(e.target.value)}
                      style={{width:"100%", minHeight:200, background:"rgba(255,255,255,0.04)", border:`1px solid ${GOLD_BOR}`,
                        borderRadius:8, padding:"12px 14px", fontSize:13, color: WHITE_MED, lineHeight:1.6,
                        resize:"vertical", outline:"none", boxSizing:"border-box", fontFamily:"Inter,sans-serif"}}
                    />
                  ) : (
                    <div style={{background:"white", borderRadius:8, padding:16, maxHeight:280, overflowY:"auto"}}
                      dangerouslySetInnerHTML={{__html: newBody.includes("<") ? newBody : newBody.replace(/\n/g,"<br/>")}}
                    />
                  )
                )}
              </div>
            )}

            {/* Sin IA — escribir manual */}
            {!newBody && !newDraftLoading && (
              <div style={{marginBottom:14}}>
                <label style={{fontSize:11, color: GOLD, letterSpacing:"1px", textTransform:"uppercase", display:"block", marginBottom:5}}>Mensaje (manual)</label>
                <textarea
                  placeholder="Escribe tu mensaje aquí..."
                  value={newBody}
                  onChange={e => setNewBody(e.target.value)}
                  style={{width:"100%", minHeight:140, background:"rgba(255,255,255,0.04)", border:`1px solid rgba(255,255,255,0.1)`,
                    borderRadius:8, padding:"12px 14px", fontSize:13, color: WHITE_MED, lineHeight:1.6,
                    resize:"vertical", outline:"none", boxSizing:"border-box", fontFamily:"Inter,sans-serif"}}
                />
              </div>
            )}

            {newSendSuccess && (
              <div style={{padding:"10px 14px", borderRadius:8, fontSize:13, fontWeight:600, marginBottom:12, textAlign:"center",
                background: newSendSuccess.startsWith("✅") ? "rgba(46,204,113,0.1)" : "rgba(231,76,60,0.1)",
                color: newSendSuccess.startsWith("✅") ? "#2ecc71" : "#e74c3c",
                border:`1px solid ${newSendSuccess.startsWith("✅") ? "rgba(46,204,113,0.25)" : "rgba(231,76,60,0.25)"}`}}>
                {newSendSuccess}
              </div>
            )}

            {/* Botones enviar */}
            <div style={{display:"flex", gap:8, justifyContent:"flex-end"}}>
              <button onClick={() => { setShowNewEmail(false); setNewTo(""); setNewSubject(""); setNewBody(""); }}
                style={{padding:"9px 18px", borderRadius:7, fontSize:13, fontWeight:600, background:"rgba(255,255,255,0.06)", color: WHITE_DIM, border:"none", cursor:"pointer"}}>
                Cancelar
              </button>
              <button onClick={sendNewEmail} disabled={newSendLoading || !newBody.trim()}
                style={{padding:"9px 22px", borderRadius:7, fontSize:13, fontWeight:700, background: GOLD, color:"#0d1b3e",
                  border:"none", cursor: newSendLoading ? "not-allowed" : "pointer", opacity: newSendLoading || !newBody.trim() ? 0.6 : 1}}>
                {newSendLoading ? "Enviando..." : "📤 Enviar Correo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Hook para contar correos nuevos ──
function useInboxCount() {
  const [count, setCount] = useState(0);
  useEffect(() => {
    const fetchCount = async () => {
      try {
        const data = await get("/email/inbox");
        const emails = data.messages || data.emails || [];
        const real = emails.filter(e => !isSystemEmail(e.from || e.sender || e.from_email || ""));
        setCount(real.length);
      } catch {}
    };
    fetchCount();
    const interval = setInterval(fetchCount, 60000); // cada 60s
    return () => clearInterval(interval);
  }, []);
  return count;
}

export default function App() {
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const handleLogin = (t, u) => { setToken(t); setUsername(u); };
  const handleLogout = () => { localStorage.removeItem("token"); localStorage.removeItem("username"); setToken(""); };
  const [view, setView] = useState("projects");
  const [projectTab, setProjectTab] = useState("normal");
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [pendingProject, setPendingProject] = useState(null);
  const [keyInput, setKeyInput] = useState("");
  const [keyError, setKeyError] = useState("");
  const [ganttView, setGanttView] = useState("gantt");
  const [projects, setProjects] = useState([]);
  const [sel, setSel] = useState(null);
  const [fins, setFins] = useState([]);
  const [kpis, setKpis] = useState([]);
  const [ai, setAi] = useState("");
  const [aiLoad, setAiLoad] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [np, setNp] = useState({name:"",description:"",start_date:"",end_date:"",budget:0,currency:"USD"});
  const [nf, setNf] = useState({category:"",type:"income",amount:0,date:"",description:"",month:new Date().getMonth()+1,year:new Date().getFullYear()});
  const [nk, setNk] = useState({name:"",value:0,target:0,unit:"",category:"",date:new Date().toISOString().split("T")[0]});
  const [showNP, setShowNP] = useState(false);
  const [showNF, setShowNF] = useState(false);
  const [showNK, setShowNK] = useState(false);
  const [showModules, setShowModules] = useState(false);
  const [showInbox, setShowInbox] = useState(false);

  // Badge de correos nuevos
  const inboxCount = useInboxCount();

  useEffect(() => { loadP(); }, []);
  useEffect(() => { if (sel) { loadF(); loadK(); } }, [sel]);

  const loadP = async () => setProjects(await get("/projects/"));
  const loadF = async () => { if(sel) setFins(await get("/projects/"+sel.id+"/financials")); };
  const loadK = async () => { if(sel) setKpis(await get("/projects/"+sel.id+"/kpis")); };

  const createP = async () => { await post("/projects/",{...np,start_date:np.start_date+"T00:00:00",end_date:np.end_date+"T00:00:00"}); setShowNP(false); loadP(); };
  const createF = async () => { if(!sel) return; await post("/projects/"+sel.id+"/financials",{...nf,date:nf.date+"T00:00:00"}); setShowNF(false); loadF(); };
  const createK = async () => { if(!sel) return; await post("/projects/"+sel.id+"/kpis",{...nk,date:nk.date+"T00:00:00"}); setShowNK(false); loadK(); };
  const delF = async (id) => { if(!sel) return; await del("/projects/"+sel.id+"/financials/"+id); loadF(); };

  const getAI = async (type) => { if(!sel) return; setAiLoad(true); setAi(""); setView("ai"); try { const r = await get("/projects/"+sel.id+"/analysis/"+type); setAi(r.analysis); } catch(e) { setAi("Error con la IA"); } setAiLoad(false); };

  const sendChat = async () => {
    if (!chatInput.trim() || !sel) return;
    const userMsg = {role:"user", content: chatInput};
    const newMessages = [...chatMessages, userMsg];
    setChatMessages(newMessages);
    setChatInput("");
    setChatLoading(true);
    try {
      const r = await post("/projects/"+sel.id+"/chat", {messages: newMessages});
      setChatMessages([...newMessages, {role:"assistant", content: r.response}]);
    } catch(e) {
      setChatMessages([...newMessages, {role:"assistant", content: "Error al conectar con la IA."}]);
    }
    setChatLoading(false);
  };

  const income = fins.filter(f=>f.type==="income").reduce((a,b)=>a+b.amount,0);
  const expense = fins.filter(f=>f.type==="expense").reduce((a,b)=>a+b.amount,0);
  const chartD = Array.from({length:12},(_,i)=>({mes:["Ene","Feb","Mar","Abr","May","Jun","Jul","Ago","Sep","Oct","Nov","Dic"][i],ingresos:fins.filter(f=>f.type==="income"&&f.month===i+1).reduce((a,b)=>a+b.amount,0),gastos:fins.filter(f=>f.type==="expense"&&f.month===i+1).reduce((a,b)=>a+b.amount,0)})).filter(d=>d.ingresos>0||d.gastos>0);

  const NAV_ITEMS = sel ? [
    ["gantt","📊 Gantt"],
    ["financials","💰 Finanzas"],
    ["kpis","🎯 KPIs"],
    ["ai","🤖 Análisis"],
    ["chat","💬 Chat IA"],
    ["knowledge","🧠 Base de Conocimiento"],
    ["dashboard","📈 Dashboard"],
    ["operativo","🏭 Operativo"],
  ] : [];

  if (!token) return <Login onLogin={handleLogin} />;

  const openSecureProject = (p) => { setPendingProject(p); setKeyInput(""); setKeyError(""); setShowKeyModal(true); };
  const confirmKey = () => { if (keyInput === "dc2026") { setSel(pendingProject); setView("gantt"); setShowKeyModal(false); } else { setKeyError("Clave incorrecta"); } };

  const initials = username ? username.slice(0,2).toUpperCase() : "DC";

  return (
    <div style={{fontFamily:"Inter, -apple-system, sans-serif", background: PAGE_BG, minHeight:"100vh"}}>

      {/* ── NAVBAR PRINCIPAL ── */}
      <div style={{
        background: NAV_BG,
        borderBottom: `1px solid ${GOLD_BOR}`,
        padding:"0 24px",
        height:60,
        display:"flex",
        alignItems:"center",
        gap:0,
        position:"sticky",
        top:0,
        zIndex:100,
        boxShadow:"0 1px 24px rgba(0,0,0,0.45)",
      }}>
        {/* Logo */}
        <div style={{display:"flex", alignItems:"center", gap:10, minWidth:180, marginRight:20}}>
          <LogoIcon />
          <div style={{display:"flex", flexDirection:"column", lineHeight:1.1}}>
            <span style={{fontSize:15, fontWeight:600, color: GOLD, letterSpacing:"0.3px"}}>DC Project</span>
            <span style={{fontSize:9, color:"rgba(255,255,255,0.35)", letterSpacing:"1.8px", textTransform:"uppercase"}}>Intelligence</span>
          </div>
        </div>

        {/* Divisor */}
        <div style={{width:1, height:28, background:"rgba(255,255,255,0.1)", margin:"0 16px"}}/>

        {/* Tabs de módulos del proyecto — dropdown */}
        {sel && (
          <div style={{position:"relative", marginRight:8}}>
            <button
              onClick={() => setShowModules(!showModules)}
              style={{...navTab(true), gap:8, paddingRight:10}}>
              {NAV_LABELS[view] || "📊 Módulos"}
              <span style={{fontSize:10, opacity:0.7}}>{showModules ? "▲" : "▼"}</span>
            </button>
            {showModules && (
              <div style={{
                position:"absolute", top:"calc(100% + 8px)", left:0,
                background:"#0d1b3e",
                border:`1px solid ${GOLD_BOR}`,
                borderRadius:10,
                boxShadow:"0 8px 32px rgba(0,0,0,0.5)",
                zIndex:200,
                minWidth:210,
                overflow:"hidden",
              }}>
                {NAV_ITEMS.map(([v,l]) => (
                  <button key={v} onClick={() => { setView(v); setShowModules(false); }} style={{
                    display:"block", width:"100%", textAlign:"left",
                    padding:"10px 16px", fontSize:13, fontWeight:500,
                    background: view===v ? GOLD_DIM : "transparent",
                    color: view===v ? GOLD : WHITE_DIM,
                    border:"none", borderBottom:`1px solid rgba(255,255,255,0.05)`,
                    cursor:"pointer",
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background=GOLD_DIM}
                  onMouseLeave={e=>e.currentTarget.style.background=view===v?GOLD_DIM:"transparent"}>
                    {l}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Divisor derecho */}
        <div style={{marginLeft:"auto", display:"flex", alignItems:"center", gap:6}}>
          <button onClick={()=>{setSel(null);setView("projects");}} style={navTab(view==="projects" && !sel)}>
            📁 Proyectos
          </button>
          <button onClick={()=>{setSel(null);setView("dashboard");}} style={navTab(view==="dashboard" && !sel)}>
            📈 Dashboard
          </button>
          <button onClick={()=>{setSel(null);setView("documentos");}} style={navTab(view==="documentos")}>
            📄 Mis Documentos
          </button>
          <button onClick={()=>{setSel(null);setView("roles");}} style={navTab(view==="roles")}>👥 Roles</button>
          <button onClick={()=>{setSel(null);setView("diplomado");}} style={navTab(view==="diplomado")}>🎓 Diplomado</button>
          <button onClick={()=>{setSel(null);setView("alimareny");}} style={navTab(view==="alimareny")}>🗽 Alimare NY</button>

          {/* ── BOTÓN INBOX con badge ── */}
          <button
            onClick={() => setShowInbox(true)}
            style={{
              ...navTab(false),
              position:"relative",
              padding:"6px 13px",
            }}>
            📬 Inbox
            {inboxCount > 0 && (
              <span style={{
                position:"absolute",
                top:-4, right:-4,
                minWidth:16, height:16,
                background:"#e74c3c",
                borderRadius:20,
                fontSize:10, fontWeight:800,
                color:"white",
                display:"flex", alignItems:"center", justifyContent:"center",
                padding:"0 4px",
                boxShadow:"0 0 0 2px #0a1628",
                animation:"pulse 2s infinite",
              }}>
                {inboxCount > 9 ? "9+" : inboxCount}
              </span>
            )}
          </button>

          {/* Divisor */}
          <div style={{width:1, height:24, background:"rgba(255,255,255,0.1)", margin:"0 8px"}}/>

          {/* Avatar usuario */}
          <div style={{
            display:"flex", alignItems:"center", gap:7,
            padding:"4px 10px", borderRadius:20,
            background:"rgba(255,255,255,0.05)",
            border:"1px solid rgba(255,255,255,0.08)",
          }}>
            <div style={{
              width:24, height:24, borderRadius:"50%",
              background:"rgba(212,175,55,0.2)",
              border:`1px solid ${GOLD_BOR}`,
              display:"flex", alignItems:"center", justifyContent:"center",
              fontSize:10, fontWeight:700, color: GOLD,
            }}>{initials}</div>
            <span style={{fontSize:12, color: WHITE_DIM}}>{username || "dcantos"}</span>
          </div>

          {/* Botón salir */}
          <button onClick={handleLogout} style={{
            padding:"6px 14px", borderRadius:6,
            fontSize:12, fontWeight:600,
            color:"#0d1b3e", background: GOLD,
            border:"none", cursor:"pointer",
          }}>Salir</button>
        </div>
      </div>

      {/* Animación pulse para badge */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.15); opacity: 0.85; }
        }
      `}</style>

      {/* ── CONTENIDO ── */}
      <div style={{padding:24, maxWidth:1400, margin:"0 auto"}}>

        {view==="projects" && (
          <div>
            <div style={{display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20}}>
              <div>
                <p style={{fontSize:11, color: GOLD, letterSpacing:"2px", textTransform:"uppercase", marginBottom:4}}>Proyectos Activos</p>
                <div style={{display:"flex", gap:6}}>
                  <button onClick={()=>setProjectTab("normal")} style={{padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:600,fontSize:12,background:projectTab==="normal"?GOLD:"rgba(255,255,255,0.06)",color:projectTab==="normal"?"#0d1b3e": WHITE_DIM}}>Todos</button>
                  <button onClick={()=>setProjectTab("secure")} style={{padding:"6px 14px",borderRadius:6,border:"none",cursor:"pointer",fontWeight:600,fontSize:12,background:projectTab==="secure"?"#7c3aed":"rgba(255,255,255,0.06)",color:projectTab==="secure"?"white": WHITE_DIM}}>🔒 Seguros</button>
                </div>
              </div>
              <button onClick={()=>setShowNP(true)} style={{...btn, background: GOLD, color:"#0d1b3e", fontWeight:700}}>+ Nuevo Proyecto</button>
            </div>

            {showNP && (
              <div style={{...darkCard, marginBottom:20}}>
                <h3 style={{fontWeight:700,marginBottom:16,color: WHITE_MED}}>Nuevo Proyecto</h3>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <input style={inp} placeholder="Nombre" value={np.name} onChange={e=>setNp({...np,name:e.target.value})}/>
                  <input style={inp} placeholder="Descripción" value={np.description} onChange={e=>setNp({...np,description:e.target.value})}/>
                  <div><label style={{fontSize:12,color:"#94A3B8"}}>Inicio</label><input type="date" style={inp} value={np.start_date} onChange={e=>setNp({...np,start_date:e.target.value})}/></div>
                  <div><label style={{fontSize:12,color:"#94A3B8"}}>Fin</label><input type="date" style={inp} value={np.end_date} onChange={e=>setNp({...np,end_date:e.target.value})}/></div>
                  <input type="number" style={inp} placeholder="Presupuesto" value={np.budget} onChange={e=>setNp({...np,budget:Number(e.target.value)})}/>
                  <select style={inp} value={np.currency} onChange={e=>setNp({...np,currency:e.target.value})}><option value="USD">USD</option><option value="EUR">EUR</option><option value="MXN">MXN</option></select>
                </div>
                <div style={{display:"flex",gap:8,marginTop:16}}><button onClick={createP} style={{...btn,background:GOLD,color:"#0d1b3e"}}>Crear</button><button onClick={()=>setShowNP(false)} style={btnG}>Cancelar</button></div>
              </div>
            )}

            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))",gap:16}}>
              {projects.filter(p=>projectTab==="secure"?p.is_secure:!p.is_secure).map(p=>(
                <div key={p.id} onClick={()=>{ if(p.is_secure){openSecureProject(p);}else{setSel(p);setView("gantt");setGanttView("gantt");}}}
                  style={{...darkCard, cursor:"pointer", transition:"all 0.15s"}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor=GOLD_BOR; e.currentTarget.style.background="rgba(212,175,55,0.05)";}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor="rgba(255,255,255,0.07)"; e.currentTarget.style.background="rgba(255,255,255,0.03)";}}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:10}}>
                    <div style={{width:34,height:34,borderRadius:8,background:"rgba(212,175,55,0.1)",border:`1px solid ${GOLD_BOR}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16}}>📁</div>
                    <span style={{background:"rgba(46,204,113,0.12)",color:"#2ecc71",borderRadius:20,padding:"3px 10px",fontSize:11,fontWeight:600,alignSelf:"flex-start"}}>{p.status}</span>
                  </div>
                  <h3 style={{fontWeight:700,color: WHITE_MED, fontSize:14, marginBottom:4}}>{p.name}</h3>
                  <p style={{color:"rgba(255,255,255,0.35)",fontSize:12,marginBottom:12}}>{p.description}</p>
                  <div style={{display:"flex",justifyContent:"space-between",fontSize:11,color:"rgba(255,255,255,0.25)"}}>
                    <span>📅 {new Date(p.start_date).toLocaleDateString()}</span>
                    <span style={{fontWeight:700,color: GOLD}}>{p.currency} {p.budget.toLocaleString()}</span>
                  </div>
                </div>
              ))}
              {projects.length===0 && <div style={{textAlign:"center",color:"rgba(255,255,255,0.25)",padding:40,gridColumn:"1/-1"}}>Crea tu primer proyecto</div>}
            </div>
          </div>
        )}

        {view==="gantt" && sel && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED,display:"flex",alignItems:"center",gap:10}}>
                {sel.id === 3 ? (
                  <svg width="28" height="28" viewBox="0 0 64 64" fill="none">
                    <path d="M48 10c-2 0-4 1-5 3-4-2-9-2-13 1-5 3-8 8-8 14 0 3 1 6 2 8-3 1-5 3-6 6-1 2-1 5 1 7 1 2 4 3 6 2 1 0 2-1 2-2" stroke="#D4AF37" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M20 44c2 2 5 3 8 3 6 0 11-4 13-10" stroke="#D4AF37" strokeWidth="2.2" strokeLinecap="round"/>
                    <path d="M35 14c1 3 1 6 0 9-1 4-4 7-7 9" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M43 13c0 4-1 7-3 10" stroke="#D4AF37" strokeWidth="2" strokeLinecap="round"/>
                    <path d="M22 42l-4 6M25 45l-2 5" stroke="#D4AF37" strokeWidth="1.8" strokeLinecap="round"/>
                    <circle cx="30" cy="22" r="1.5" fill="#D4AF37" opacity="0.7"/>
                  </svg>
                ) : "📊"} {sel.name}
              </h2>
              <div style={{display:"flex",gap:8}}>
                <button onClick={()=>setGanttView("builder")} style={{...btn,background:ganttView==="builder"?"#7C3AED":"#2563EB"}}>🤖 Generar con IA</button>
                <button onClick={()=>setGanttView("gantt")} style={btnG}>📊 Ver Gantt</button>
              </div>
            </div>
            {ganttView==="builder" ? <AIGanttBuilder projectId={sel.id} projectName={sel.name} onTasksGenerated={()=>setGanttView("gantt")} /> : <GanttChart projectId={sel.id} projectName={sel.name} projectStart={sel.start_date} projectEnd={sel.end_date} currency={sel.currency} />}
          </div>
        )}

        {view==="financials" && sel && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED}}>💰 Finanzas — {sel.name}</h2>
              <button onClick={()=>setShowNF(true)} style={{...btn,background:GOLD,color:"#0d1b3e"}}>+ Nuevo Registro</button>
            </div>
            <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:16,marginBottom:20}}>
              {[["Ingresos",income,"#2ecc71"],["Gastos",expense,"#e74c3c"],["Utilidad",income-expense,income-expense>=0?GOLD:"#e74c3c"]].map(([l,v,c])=>(
                <div key={l} style={darkCard}><div style={{fontSize:12,color:"rgba(255,255,255,0.4)",marginBottom:8,letterSpacing:"1px",textTransform:"uppercase"}}>{l}</div><div style={{fontSize:26,fontWeight:800,color:c}}>{sel.currency} {Number(v).toLocaleString()}</div></div>
              ))}
            </div>
            {showNF && (
              <div style={{...darkCard,marginBottom:20}}>
                <h3 style={{fontWeight:700,marginBottom:16,color: WHITE_MED}}>Nuevo Registro</h3>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  <input style={inp} placeholder="Categoría" value={nf.category} onChange={e=>setNf({...nf,category:e.target.value})}/>
                  <select style={inp} value={nf.type} onChange={e=>setNf({...nf,type:e.target.value})}><option value="income">Ingreso</option><option value="expense">Gasto</option></select>
                  <input type="number" style={inp} placeholder="Monto" value={nf.amount} onChange={e=>setNf({...nf,amount:Number(e.target.value)})}/>
                  <input style={inp} placeholder="Descripción" value={nf.description} onChange={e=>setNf({...nf,description:e.target.value})}/>
                  <div><label style={{fontSize:12,color:"#94A3B8"}}>Fecha</label><input type="date" style={inp} value={nf.date} onChange={e=>setNf({...nf,date:e.target.value})}/></div>
                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8}}>
                    <input type="number" style={inp} placeholder="Mes" value={nf.month} onChange={e=>setNf({...nf,month:Number(e.target.value)})}/>
                    <input type="number" style={inp} placeholder="Año" value={nf.year} onChange={e=>setNf({...nf,year:Number(e.target.value)})}/>
                  </div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:16}}><button onClick={createF} style={{...btn,background:GOLD,color:"#0d1b3e"}}>Guardar</button><button onClick={()=>setShowNF(false)} style={btnG}>Cancelar</button></div>
              </div>
            )}
            {chartD.length>0 && (
              <div style={{...card,marginBottom:20}}>
                <h3 style={{fontWeight:700,marginBottom:16}}>Ingresos vs Gastos</h3>
                <ResponsiveContainer width="100%" height={260}><BarChart data={chartD}><CartesianGrid strokeDasharray="3 3"/><XAxis dataKey="mes"/><YAxis/><Tooltip/><Legend/><Bar dataKey="ingresos" fill="#10B981" radius={[4,4,0,0]}/><Bar dataKey="gastos" fill="#EF4444" radius={[4,4,0,0]}/></BarChart></ResponsiveContainer>
              </div>
            )}
            <div style={darkCard}>
              {fins.map(f=>(<div key={f.id} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"10px 0",borderBottom:"1px solid rgba(255,255,255,0.05)"}}>
                <div><div style={{fontWeight:600,fontSize:14,color: WHITE_MED}}>{f.category}</div><div style={{fontSize:12,color:"rgba(255,255,255,0.3)"}}>{f.description} · {f.month}/{f.year}</div></div>
                <div style={{display:"flex",alignItems:"center",gap:12}}>
                  <span style={{fontWeight:700,color:f.type==="income"?"#2ecc71":"#e74c3c"}}>{f.type==="income"?"+":"-"}{sel.currency} {f.amount.toLocaleString()}</span>
                  <button onClick={()=>delF(f.id)} style={{background:"none",border:"none",cursor:"pointer",color:"#e74c3c"}}>✕</button>
                </div>
              </div>))}
              {fins.length===0 && <div style={{textAlign:"center",color:"rgba(255,255,255,0.25)",padding:30}}>Sin registros</div>}
            </div>
          </div>
        )}

        {view==="kpis" && sel && (
          <div>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED}}>🎯 KPIs — {sel.name}</h2>
              <button onClick={()=>setShowNK(true)} style={{...btn,background:GOLD,color:"#0d1b3e"}}>+ Nuevo KPI</button>
            </div>
            {showNK && (
              <div style={{...darkCard,marginBottom:20}}>
                <h3 style={{fontWeight:700,marginBottom:16,color: WHITE_MED}}>Nuevo KPI</h3>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12}}>
                  <input style={inp} placeholder="Nombre KPI" value={nk.name} onChange={e=>setNk({...nk,name:e.target.value})}/>
                  <input style={inp} placeholder="Categoría" value={nk.category} onChange={e=>setNk({...nk,category:e.target.value})}/>
                  <input style={inp} placeholder="Unidad" value={nk.unit} onChange={e=>setNk({...nk,unit:e.target.value})}/>
                  <input type="number" style={inp} placeholder="Valor actual" value={nk.value} onChange={e=>setNk({...nk,value:Number(e.target.value)})}/>
                  <input type="number" style={inp} placeholder="Meta" value={nk.target} onChange={e=>setNk({...nk,target:Number(e.target.value)})}/>
                  <div><label style={{fontSize:12,color:"#94A3B8"}}>Fecha</label><input type="date" style={inp} value={nk.date} onChange={e=>setNk({...nk,date:e.target.value})}/></div>
                </div>
                <div style={{display:"flex",gap:8,marginTop:16}}><button onClick={createK} style={{...btn,background:GOLD,color:"#0d1b3e"}}>Guardar</button><button onClick={()=>setShowNK(false)} style={btnG}>Cancelar</button></div>
              </div>
            )}
            <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:16}}>
              {kpis.map((k,i)=>{ const pct=Math.min((k.value/k.target)*100,100); const ok=pct>=80; return (
                <div key={k.id} style={darkCard}>
                  <div style={{display:"flex",justifyContent:"space-between",marginBottom:8}}>
                    <span style={{fontSize:11,color:"rgba(255,255,255,0.35)",fontWeight:600,letterSpacing:"1px"}}>{k.category.toUpperCase()}</span>
                    <span style={{background:ok?"rgba(46,204,113,0.12)":"rgba(212,175,55,0.12)",color:ok?"#2ecc71":GOLD,borderRadius:20,padding:"2px 8px",fontSize:11,fontWeight:600}}>{ok?"✔ Meta":"⚡ Prog."}</span>
                  </div>
                  <div style={{fontWeight:700,fontSize:14,color: WHITE_MED,marginBottom:4}}>{k.name}</div>
                  <div style={{fontSize:26,fontWeight:800,color:COLORS[i%COLORS.length]}}>{k.value} <span style={{fontSize:13,color:"rgba(255,255,255,0.3)"}}>{k.unit}</span></div>
                  <div style={{fontSize:12,color:"rgba(255,255,255,0.3)",marginBottom:8}}>Meta: {k.target} {k.unit}</div>
                  <div style={{background:"rgba(255,255,255,0.07)",borderRadius:20,height:6}}><div style={{background:ok?"#2ecc71":GOLD,height:6,borderRadius:20,width:pct+"%",transition:"width 0.3s"}}/></div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.3)",marginTop:4,textAlign:"right"}}>{pct.toFixed(1)}%</div>
                </div>
              );})}
            </div>
            {kpis.length===0 && <div style={{textAlign:"center",color:"rgba(255,255,255,0.25)",padding:40}}>Sin KPIs aún</div>}
          </div>
        )}

        {view==="ai" && sel && (
          <div>
            <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED,marginBottom:20}}>🤖 Análisis IA — {sel.name}</h2>
            <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:12,marginBottom:24}}>
              {[["general","📊","Análisis General","Resumen ejecutivo"],["financial","💰","Financiero","ROI y proyecciones"],["timeline","📅","Cronograma","Ruta crítica"],["kpi","🎯","KPIs","Recomendaciones"]].map(([type,icon,title,desc])=>(
                <button key={type} onClick={()=>getAI(type)} style={{...darkCard,cursor:"pointer",textAlign:"left",border:`1px solid rgba(255,255,255,0.07)`}}
                  onMouseEnter={e=>e.currentTarget.style.borderColor=GOLD_BOR}
                  onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.07)"}>
                  <div style={{fontSize:22,marginBottom:8}}>{icon}</div>
                  <div style={{fontWeight:700,color: WHITE_MED,fontSize:13}}>{title}</div>
                  <div style={{fontSize:11,color:"rgba(255,255,255,0.35)"}}>{desc}</div>
                </button>
              ))}
            </div>
            <div style={{...darkCard,minHeight:300}}>
              {aiLoad ? <div style={{textAlign:"center",padding:60}}><div style={{fontSize:36}}>🤖</div><div style={{color:GOLD,fontWeight:600,marginTop:16}}>Analizando...</div></div>
              : ai ? <div style={{whiteSpace:"pre-wrap",lineHeight:1.7,color: WHITE_MED,fontSize:14}}>{ai}</div>
              : <div style={{textAlign:"center",padding:60,color:"rgba(255,255,255,0.2)"}}><div style={{fontSize:40}}>🤖</div><div style={{marginTop:16}}>Selecciona un análisis arriba</div></div>}
            </div>
          </div>
        )}

        {view==="chat" && sel && (
          <div>
            <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED,marginBottom:8}}>💬 Chat IA — {sel.name}</h2>
            <p style={{color:"rgba(255,255,255,0.35)",fontSize:13,marginBottom:20}}>Pregúntame sobre finanzas, proyecciones o cualquier aspecto de tu proyecto.</p>
            <div style={darkCard}>
              <div style={{height:420,overflowY:"auto",marginBottom:16,display:"flex",flexDirection:"column",gap:12}}>
                {chatMessages.length===0 && (
                  <div style={{textAlign:"center",padding:40,color:"rgba(255,255,255,0.25)"}}>
                    <div style={{fontSize:36,marginBottom:12}}>💬</div>
                    <div style={{fontWeight:600,marginBottom:8,color: WHITE_MED}}>Chat con tu Agente IA Experto</div>
                    <div style={{fontSize:13,marginBottom:20}}>Tengo acceso a todos los datos de tu proyecto.</div>
                    <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,maxWidth:500,margin:"0 auto",textAlign:"left"}}>
                      {["¿Cuál es el ROI proyectado?","Calcula el punto de equilibrio","¿Qué tareas están en ruta crítica?","Proyecta mis ingresos a 12 meses"].map(q=>(
                        <div key={q} onClick={()=>setChatInput(q)} style={{background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.08)",borderRadius:8,padding:"8px 12px",fontSize:12,cursor:"pointer",color:"rgba(255,255,255,0.5)"}}
                          onMouseEnter={e=>e.currentTarget.style.borderColor=GOLD_BOR}
                          onMouseLeave={e=>e.currentTarget.style.borderColor="rgba(255,255,255,0.08)"}>
                          💡 {q}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {chatMessages.map((msg,i)=>(
                  <div key={i} style={{display:"flex",justifyContent:msg.role==="user"?"flex-end":"flex-start"}}>
                    <div style={{maxWidth:"80%",padding:"12px 16px",borderRadius:msg.role==="user"?"16px 16px 4px 16px":"16px 16px 16px 4px",background:msg.role==="user"? GOLD_DIM :"rgba(255,255,255,0.05)",color: WHITE_MED,fontSize:13,lineHeight:1.6,whiteSpace:"pre-wrap",border:`1px solid ${msg.role==="user"?GOLD_BOR:"rgba(255,255,255,0.07)"}`}}>
                      {msg.content}
                    </div>
                  </div>
                ))}
                {chatLoading && <div style={{display:"flex",justifyContent:"flex-start"}}><div style={{background:"rgba(255,255,255,0.05)",padding:"12px 16px",borderRadius:"16px 16px 16px 4px",fontSize:13,color:"rgba(255,255,255,0.4)"}}>🤖 Analizando...</div></div>}
              </div>
              <div style={{display:"flex",gap:8}}>
                <input style={{...inp,flex:1,padding:"12px 16px",background:"rgba(255,255,255,0.04)",border:"1px solid rgba(255,255,255,0.1)",color:"white"}} placeholder="Pregunta sobre tu proyecto..." value={chatInput} onChange={e=>setChatInput(e.target.value)} onKeyDown={e=>{if(e.key==="Enter"&&!e.shiftKey){e.preventDefault();sendChat();}}}/>
                <button onClick={sendChat} disabled={chatLoading||!chatInput.trim()} style={{...btn,padding:"12px 20px",background:GOLD,color:"#0d1b3e",opacity:chatLoading||!chatInput.trim()?0.5:1}}>Enviar →</button>
              </div>
            </div>
          </div>
        )}

        {view==="dashboard" && <Dashboard />}
        {view==="documentos" && <MisDocumentos />}
        {view==="roles" && <ParticipantRoles />}
        {view==="diplomado" && <DiplomadoChat />}
        {view==="alimareny" && <AlimareNY />}
        {view==="operativo" && <div><DashboardOperativo /></div>}
        {view==="dashboard" && <div><DashboardCamaron /></div>}

        {view==="knowledge" && (
          <div>
            <h2 style={{fontSize:20,fontWeight:700,color: WHITE_MED,marginBottom:8}}>🧠 Base de Conocimiento</h2>
            <p style={{color:"rgba(255,255,255,0.35)",fontSize:13,marginBottom:20}}>Sube manuales, contratos, normativas y documentos. La IA los usará para responder preguntas específicas de tu negocio.</p>
            <KnowledgeBase projectId={sel?.id} />
          </div>
        )}

      </div>

      {/* ── MODAL PROYECTO SEGURO ── */}
      {showKeyModal && (
        <div style={{position:"fixed",top:0,left:0,width:"100%",height:"100%",background:"rgba(0,0,0,0.75)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:9999}}>
          <div style={{background:"#0d1b3e",border:`1px solid ${GOLD_BOR}`,borderRadius:16,padding:32,width:320,boxShadow:"0 8px 40px rgba(0,0,0,0.6)"}}>
            <div style={{textAlign:"center",marginBottom:20}}>
              <div style={{fontSize:32,marginBottom:8}}>🔒</div>
              <h3 style={{margin:"8px 0",color: WHITE_MED}}>Proyecto Seguro</h3>
              <p style={{color:"rgba(255,255,255,0.35)",fontSize:13}}>{pendingProject && pendingProject.name}</p>
            </div>
            <input type="password" value={keyInput} onChange={e=>setKeyInput(e.target.value)} onKeyDown={e=>e.key==="Enter"&&confirmKey()} placeholder="Ingresa la clave" style={{width:"100%",padding:"10px 14px",background:"rgba(255,255,255,0.05)",border:`1px solid ${GOLD_BOR}`,borderRadius:8,fontSize:14,outline:"none",boxSizing:"border-box",marginBottom:8,color:"white"}}/>
            {keyError && <div style={{color:"#e74c3c",fontSize:12,marginBottom:8,textAlign:"center"}}>{keyError}</div>}
            <div style={{display:"flex",gap:8,marginTop:8}}>
              <button onClick={confirmKey} style={{flex:1,padding:"10px",background:GOLD,color:"#0d1b3e",border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>Entrar</button>
              <button onClick={()=>setShowKeyModal(false)} style={{flex:1,padding:"10px",background:"rgba(255,255,255,0.08)",color: WHITE_DIM,border:"none",borderRadius:8,cursor:"pointer",fontWeight:700}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* ── PANEL INBOX ── */}
      {showInbox && <InboxPanel onClose={() => setShowInbox(false)} />}

    </div>
  );
}
